import { useEffect, useState } from 'react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { KpiCard } from '../components/dashboard/KpiCard';
import { DollarSign, Phone, Mail, Linkedin, Calendar as CalIcon, FileText, Award, Percent, Wallet } from 'lucide-react';
import { fmtCurrency, fmtNumber, fmtDate, STATUS_LABELS, STATUS_COLORS } from '../lib/utils';
import { Skeleton } from '../components/ui/skeleton';
import { FavoritesSection } from '../components/dashboard/FavoritesSection';
import { AnnouncementsPanel } from '../components/dashboard/AnnouncementsPanel';

export default function PersonalDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [goals, setGoals] = useState(null);

  useEffect(() => {
    if (!user) return;
    Promise.all([api.get(`/analytics/personal/${user.id}`), api.get('/goals?period=monthly')]).then(([d, g]) => {
      setData(d.data);
      setGoals(g.data);
    });
  }, [user]);

  if (!data || !goals) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {[...Array(8)].map((_, i) => (
          <Skeleton key={i} className="h-36 rounded-2xl" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="personal-dashboard">
      <AnnouncementsPanel />
      <FavoritesSection />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <KpiCard label="Revenue Generated" value={fmtCurrency(data.revenue)} rawValue={data.revenue} goal={goals.revenue} icon={DollarSign} testid="my-kpi-revenue" />
        <KpiCard label="Outreach Completed" value={fmtNumber(data.outreach)} goal={goals.outreach} icon={Mail} accent="navy" testid="my-kpi-outreach" />
        <KpiCard label="Calls Made" value={data.calls} goal={goals.calls} icon={Phone} testid="my-kpi-calls" />
        <KpiCard label="Emails Sent" value={data.emails} goal={goals.emails} icon={Mail} accent="navy" testid="my-kpi-emails" />
        <KpiCard label="LinkedIn Outreach" value={data.linkedin} goal={goals.linkedin} icon={Linkedin} testid="my-kpi-linkedin" />
        <KpiCard label="Meetings Scheduled" value={data.meetings} goal={goals.discovery_meetings} icon={CalIcon} accent="navy" testid="my-kpi-meetings" />
        <KpiCard label="Proposals Sent" value={data.proposals_sent} goal={goals.proposals} icon={FileText} testid="my-kpi-proposals" />
        <KpiCard label="Media Partners Closed" value={data.media_partners_closed} goal={goals.media_partner_closures} icon={Award} accent="navy" testid="my-kpi-closed" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <KpiCard label="Conversion Rate" value={data.conversion_rate} suffix="%" icon={Percent} testid="my-kpi-conversion" />
        <KpiCard label="Commission Earned" value={fmtCurrency(data.commission)} icon={Wallet} accent="navy" testid="my-kpi-commission" />
        <KpiCard label="Bonus Progress" value={Math.min(100, Math.round((data.revenue / goals.revenue) * 100))} suffix="%" icon={Award} testid="my-kpi-bonus" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm" data-testid="my-tasks-card">
          <h3 className="font-heading text-lg font-bold text-navy mb-4">My Tasks</h3>
          <div className="space-y-3">
            {data.tasks.length === 0 && <div className="text-sm text-slate-500 text-center py-8">No tasks assigned</div>}
            {data.tasks.slice(0, 8).map((t) => (
              <div key={t.id} className="flex items-center gap-3 border-b border-slate-100 pb-2 last:border-0">
                <span className={`w-2 h-2 rounded-full ${t.priority === 'high' ? 'bg-magenta' : t.priority === 'medium' ? 'bg-amber-400' : 'bg-slate-300'}`} />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm text-navy truncate">{t.title}</div>
                  <div className="text-xs text-slate-500">{fmtDate(t.due_date)} · {t.status.replace('_', ' ')}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm" data-testid="my-followups-card">
          <h3 className="font-heading text-lg font-bold text-navy mb-4">Upcoming Follow-Ups</h3>
          <div className="space-y-3">
            {data.followups.length === 0 && <div className="text-sm text-slate-500 text-center py-8">No follow-ups</div>}
            {data.followups.slice(0, 8).map((l) => (
              <div key={l.id} className="flex items-center justify-between gap-3 border-b border-slate-100 pb-2 last:border-0">
                <div className="min-w-0">
                  <div className="font-medium text-sm text-navy truncate">{l.company_name}</div>
                  <div className="text-xs text-slate-500">{l.primary_contact}</div>
                </div>
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${STATUS_COLORS[l.status] || 'bg-slate-100'}`}>
                  {STATUS_LABELS[l.status]}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm lg:col-span-2" data-testid="my-meetings-card">
          <h3 className="font-heading text-lg font-bold text-navy mb-4">Upcoming Meetings</h3>
          <div className="space-y-3">
            {data.upcoming_meetings.length === 0 && <div className="text-sm text-slate-500 text-center py-8">No upcoming meetings</div>}
            {data.upcoming_meetings.map((m) => (
              <div key={m.id} className="flex items-center justify-between border-b border-slate-100 pb-2 last:border-0">
                <div>
                  <div className="font-semibold text-sm text-navy">{m.title}</div>
                  <div className="text-xs text-slate-500 capitalize">{m.event_type}</div>
                </div>
                <div className="text-xs text-slate-600 font-medium">{fmtDate(m.start_at)}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
